import { DEFAULT_BRANDING, type OrganizationBranding } from "@compliance/domain";
import { useContext, useMemo } from "react";
import type { UploadType, UserResponse } from "../api/types";
import { type UploadTypeMeta, uploadTypeLabels, uploadTypeMeta } from "../lib/uploadTypes";
import { AuthContext } from "./AuthProvider";

/**
 * The acting organization's display labels, from the `/me` grants.
 *
 * Pure so the same rule serves a component (via `useBranding`) and a plain
 * function that is handed a user. Falls back to the reference customer's
 * labels when the grant carries none — a backend predating the field, or an
 * organization with no live config — so nothing renders blank.
 */
export function brandingFor(user: UserResponse | null): OrganizationBranding {
  const grant = user?.organizations?.find((g) => g.organization_id === user.organization_id);
  return grant?.branding ?? DEFAULT_BRANDING;
}

/**
 * Reads the context directly rather than through `useAuth`, which throws
 * outside `<AuthProvider>`: a label is not worth failing a render for, and a
 * screen mounted without a session (a test, a preview) reads as the reference
 * customer — the same thing a backend predating branding would produce.
 */
export function useBranding(): OrganizationBranding {
  const ctx = useContext(AuthContext);
  return brandingFor(ctx?.user ?? null);
}

/** The upload-type registry, labelled for the acting organization. */
export function useUploadTypeMeta(): Record<UploadType, UploadTypeMeta> {
  const branding = useBranding();
  return useMemo(() => uploadTypeMeta(branding), [branding]);
}

/** Just the long labels, for the many sites that show only the type's name. */
export function useUploadTypeLabels(): Record<UploadType, string> {
  const branding = useBranding();
  return useMemo(() => uploadTypeLabels(branding), [branding]);
}
